/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// import 'swiper/css';
// import 'swiper/css/navigation';
// import 'swiper/css/pagination';
import { getTabTarget } from '../helpers/tabTarget';
import { pushDataLayer } from '../pushDataLayer';
import {
  ComponentSwiperCards,
  Enum_Componentpagescomponentswipercardsbanner_Color_Scheme,
} from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';
import { FeLink } from '$/components/FeLink';

const colorSchemes: Record<
  Enum_Componentpagescomponentswipercardsbanner_Color_Scheme,
  { light: string; dark: string }
> = {
  BLUE: { light: '#CEFFF1', dark: '#04709B' },
  WHITE_BLUE: { light: '#FFFFFF', dark: '#04709B' },
  GREEN: { light: '#E3FFCE', dark: '#5D8433' },
  WHITE_GREEN: { light: '#FFFFFF', dark: '#5D8433' },
  RED: { light: '#FFDDDD', dark: '#C70000' },
  WHITE_RED: { light: '#FFFFFF', dark: '#C70000' },
  ORANGE: { light: '#FFEBCE', dark: '#DE4E00' },
  WHITE_ORANGE: { light: '#FFFFFF', dark: '#DE4E00' },
  YELLOW: { light: '#FFFCCE', dark: '#AF6A00' },
  WHITE_YELLOW: { light: '#FFFFFF', dark: '#AF6A00' },
};

const settings = {
  breakpoints: {
    360: { slidesPerView: 1 },
    767: { slidesPerView: 1 },
    1279: { slidesPerView: 1 },
    1440: { slidesPerView: 1 },
    1920: { slidesPerView: 1 },
  },
  autoplay: {
    delay: 6000,
    pauseOnMouseEnter: true,
    disableOnInteraction: false,
    stopOnLastSlide: false,
    waitForTransition: true,
    reverseDirection: false,
  },
};

const getColorScheme = (
  colorScheme?: Enum_Componentpagescomponentswipercardsbanner_Color_Scheme,
) => {
  if (!colorScheme) return colorSchemes.BLUE;
  return colorSchemes[colorScheme];
};

export const SwiperCards: React.FC<ComponentSwiperCards> = ({ banners }) => {
  return (
    <div className="bg-swiper-cards py-8 md:pb-8 lg:pb-10 xl:pb-12">
      <div className="mx-auto max-w-screen-2xl px-spacing/padding/section-x">
        <Swiper
          speed={10000}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          autoplay={settings.autoplay}
          loop={true}
          cssMode={true}
          pagination={{ clickable: true }}
          breakpoints={settings.breakpoints}
          spaceBetween={10}
          centeredSlides={true}
          modules={[Autoplay, Navigation, Pagination]}
          className="group relative mx-auto max-w-screen-2xl"
        >
          <div className="swiper-button-prev invisible !left-2 opacity-0 transition-all duration-300 !text-black after:!size-8 lg:group-hover:visible lg:group-hover:opacity-20"></div>
          <div className="swiper-button-next invisible !right-2 opacity-0 transition-all duration-300 !text-black after:!size-8 lg:group-hover:visible lg:group-hover:opacity-20"></div>
          {banners?.map((slide, index) => (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="relative flex overflow-hidden rounded-2xl pb-10 lg:rounded-3xl"
            >
              <div
                className="grid grid-cols-1 rounded-2xl px-5 shadow-md lg:grid-cols-2 lg:rounded-3xl"
                style={{
                  backgroundColor: getColorScheme(
                    slide?.color_scheme ?? undefined,
                  ).light,
                }}
              >
                <div className="flex flex-col justify-start p-4 md:px-6 lg:justify-center lg:px-8 lg:py-9 xl:p-10">
                  {slide?.over_title && (
                    <p
                      className="mb-1 font-bold text-primary text-base leading-normal md:text-lg md:leading-line-height/lg lg:mb-2 lg:text-xl lg:leading-line-height/xl"
                      style={{
                        color: getColorScheme(slide?.color_scheme ?? undefined)
                          .dark,
                      }}
                    >
                      {slide?.over_title}
                    </p>
                  )}
                  {slide?.title && (
                    <h3 className="mb-2 font-bold text-gray-900 text-2xl leading-line-height/2xl md:text-3xl md:leading-line-height/3xl lg:mb-4 lg:text-4xl lg:leading-line-height/4xl">
                      {slide?.title}
                    </h3>
                  )}
                  <RichText className="align-middle font-medium text-gray-900 leading-normal md:text-lg md:leading-line-height/lg lg:text-xl lg:leading-line-height/xl">
                    {slide?.description ?? ''}
                  </RichText>
                  {slide?.cta_label && slide?.cta_url && (
                    <FeLink
                      href={slide?.cta_url}
                      target={getTabTarget(slide?.cta_target) ?? '_blank'}
                    >
                      <Button
                        title={slide?.cta_label}
                        type="button"
                        style={{
                          backgroundColor: getColorScheme(
                            slide?.color_scheme ?? undefined,
                          ).dark,
                        }}
                        className="mt-4 w-full lg:mt-6 lg:max-w-75 lg:font-semibold lg:text-buttons/cta lg:leading-line-height/lg"
                        onClick={() =>
                          pushDataLayer({
                            event: 'navigate',
                            component_type: 'image-carousel',
                            component_title: `${slide?.title}_${slide?.cta_label}`,
                            action: 'click',
                            index: index,
                          })
                        }
                      >
                        <span className="text-white">{slide?.cta_label}</span>
                      </Button>
                    </FeLink>
                  )}
                  {slide?.caption && (
                    <p className="mt-2 font-medium text-gray-700 text-sm leading-line-height/sm md:mt-4 md:text-lg md:leading-line-height/lg lg:mt-2 lg:text-base lg:leading-normal">
                      {slide?.caption}
                    </p>
                  )}
                </div>
                <div
                  className={`flex items-center justify-center ${slide?.padding ? 'p-4 md:px-6 lg:px-8 lg:py-9 xl:p-10' : ''}`}
                >
                  {slide?.cta_label && slide?.cta_url ? (
                    <FeLink
                      href={slide?.cta_url}
                      target={getTabTarget(slide?.cta_target) ?? '_blank'}
                      rel="noopener noreferrer"
                    >
                      <CmsImage
                        unoptimized
                        src={slide?.image?.url ?? ''}
                        alt={slide?.title ?? ''}
                        width={720}
                        height={540}
                        className="size-full object-contain sm:h-[246px] sm:w-82 md:h-[540px] md:w-[720px] lg:h-114 lg:w-[608px] xl:h-[510px] xl:w-[680px]"
                      />
                    </FeLink>
                  ) : (
                    <CmsImage
                      unoptimized
                      src={slide?.image?.url ?? ''}
                      alt={slide?.title ?? ''}
                      width={720}
                      height={540}
                      className="size-full object-contain sm:h-[246px] sm:w-82 md:h-[540px] md:w-[720px] lg:h-114 lg:w-[608px] xl:h-[510px] xl:w-[680px]"
                    />
                  )}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};
